/**
 * Legal + banking requisites of the operator behind uztender.com.
 *
 * WHY THIS MOVED HERE. `apps/web/src/lib/company.ts` was the single source of
 * truth while only the browser needed them (оферта, /requisites, /schet). The
 * server now renders a real payable PDF invoice, and the API cannot import from
 * `apps/web` — so the raw values live in `@tenders/shared` and BOTH runtimes
 * read them. `apps/web/src/lib/company.ts` still owns the presentation shape
 * (it derives the display phone through `formatPhone`); it no longer owns the
 * data. Editing a digit here changes the оферта, the requisites card and the
 * bank invoice at once, which is the point.
 *
 * VERBATIM company data — do not paraphrase, do not reformat.
 *
 * ⚠️ `legalName` is the RUSSIAN legal form «ООО», not the Uzbek «MCHJ». Both
 * name the same registered entity; «ООО "FASTAGO"» is what the оферта, the
 * privacy policy, /requisites and the Organization JSON-LD already publish, and
 * a payable document must not disagree with the offer it references.
 */

/** E.164, digits only — the `tel:` href and the source for the display form. */
export const COMPANY_PHONE_E164 = '+998901877798';

export const COMPANY_LEGAL = {
  legalName: 'ООО "FASTAGO"',
  shortName: 'FASTAGO',
  address: 'ГОРОД ТАШКЕНТ, МИРЗО УЛУГБЕКСКИЙ РАЙОН, Навнихол МФЙ, Тепамасжид кучаси, 4а-уй',
  account: '20208000805068049001',
  bankName: 'ТОШКЕНТ Ш., "ОРИЕНТ ФИНАНС" ХАТ БАНКИ',
  mfo: '01071',
  inn: '306377222',
  oked: '58290',
  vatReg: '326040208010',
  email: 'support@uztender.com',
  telegram: '@flesteyto',
  telegramUrl: 'https://t.me/flesteyto',
  website: 'https://uztender.com',
} as const;

/**
 * VAT line printed on the invoice. We are an IT Park resident, so the sale
 * carries no VAT; the РКПНДС is still required on the document because the
 * buyer's accountant needs it to book the payment.
 */
export const COMPANY_VAT_NOTE = `НДС не облагается — резидент IT Park (Гувохнома), РКПНДС ${COMPANY_LEGAL.vatReg}`;

/**
 * ИКПУ of the service we sell, and the unit it is sold in. Chosen 2026-09-15:
 * «услуга (раз)» because we sell access for a period, not hours (1540947) and
 * not pieces (1500630).
 */
export const SERVICE_IKPU = '10305001001000000';
export const SERVICE_UNIT_NAME = 'услуга (раз)';
export const SERVICE_UNIT_CODE = '1504169';

/**
 * Ordered requisites rows for the requisites card and the invoice. `label` is a
 * plain Russian caption (these are legal/banking terms with no common Uzbek
 * variant).
 *
 * ⚠️ The invoice PDF MEASURES these labels to size its label column — see
 * `invoice-pdf.ts`. «Рег. номер плательщика НДС (РКПНДС)» is 195 pt at 9 pt and
 * used to run straight into its own value in a 175 pt column. Adding a longer
 * label here is safe; hard-coding a column width there is not.
 */
export const COMPANY_REQUISITE_ROWS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Полное наименование', value: COMPANY_LEGAL.legalName },
  { label: 'Юридический адрес', value: COMPANY_LEGAL.address },
  { label: 'ИНН', value: COMPANY_LEGAL.inn },
  { label: 'Расчётный счёт (Р/С)', value: COMPANY_LEGAL.account },
  { label: 'Банк', value: COMPANY_LEGAL.bankName },
  { label: 'МФО', value: COMPANY_LEGAL.mfo },
  { label: 'ОКЭД', value: COMPANY_LEGAL.oked },
  { label: 'Рег. номер плательщика НДС (РКПНДС)', value: COMPANY_LEGAL.vatReg },
];
